<template>
    <div class="min-h-screen bg-[#11111b] text-white flex flex-col">
        <Header />

        <main class="relative flex items-center justify-center p-6 flex-1">
            <div class="absolute inset-0 bg-cover bg-center filter blur-sm z-0"
                :style="{ backgroundImage: `url(${backgroundImage})` }">
                <div class="absolute inset-0 bg-[#11111b] opacity-50"></div>
            </div>
            <div class="relative z-10 w-full max-w-5xl mx-auto flex flex-col md:flex-row gap-6 py-12">
                <!-- Image Gallery -->
                <div class="w-full md:w-1/3 flex flex-col gap-4">
                    <!-- Main Square Image -->
                    <div class="w-full aspect-square overflow-hidden rounded-md mb-4">
                        <img :src="serviceImages[currentImageIndex] || getDefaultImage" alt="Main Service"
                            class="w-full h-full object-cover cursor-pointer" @click="openPopup(serviceImages[currentImageIndex])" />
                    </div>
                    
                    <!-- Horizontal Scrolling Thumbnails -->
                    <div class="w-full overflow-x-auto">
                        <div class="flex gap-2 pb-2" style="min-width: max-content;">
                            <div v-for="(image, index) in serviceImages" :key="index"
                                class="w-20 h-20 flex-shrink-0 overflow-hidden rounded-md cursor-pointer border-2 transition-all duration-200"
                                :class="index === currentImageIndex ? 'border-cyan-400' : 'border-transparent hover:border-gray-400'"
                                @click="setCurrentImage(index)">
                                <img :src="image || getDefaultImage" alt="Thumbnail"
                                    class="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Service Details -->
                <div class="w-full md:w-2/3 bg-[#1e1e2f] bg-opacity-80 backdrop-blur-md p-6 rounded-lg shadow-lg">
                    <h1 class="text-3xl font-bold mb-4">{{ serviceData.title }}</h1>
                    <p class="text-gray-400 mb-4">{{ serviceData.description }}</p>

                    <div class="flex items-center gap-4">
                        <button class="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors"
                            @click="toggleLike">
                            <svg v-if="isLiked" xmlns="http://www.w3.org/2000/svg"
                                class="h-6 w-6 fill-current text-red-500" viewBox="0 0 24 20">
                                <path
                                    d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3c3.08 0 5.5 2.42 5.5 5.5 0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            <span>{{ likeCount }} Likes</span>
                        </button>
                    </div>

                    <!-- Tabs -->
                    <div class="flex flex-col sm:flex-row border-b border-gray-600 mt-6 mb-4">
                        <button v-for="tab in tabs" :key="tab"
                            :class="['px-2 sm:px-4 py-2 font-semibold text-sm sm:text-base', { 'border-b-2 border-cyan-400 text-cyan-400': currentTab === tab }]"
                            @click="currentTab = tab">
                            {{ tab }}
                        </button>
                    </div>

                    <!-- Tab Content -->
                    <div v-if="currentTab === 'Basic'" class="p-4">
                        <h1 class="text-2xl font-bold mb-2">Basic</h1>
                        <p class="text-lg font-semibold text-gray-400">{{ serviceData.basicPrice || '$25' }}</p>
                        <ul class="list-disc list-inside mt-4 text-gray-400">
                            <li v-for="item in serviceData.basicInfo || []" :key="item">{{ item }}</li>
                        </ul>
                        <a href="https://t.me/borin_morm" target="_blank" rel="noopener noreferrer">
                            <button
                                class="mt-4 w-full bg-cyan-600 text-white py-2 rounded hover:bg-cyan-700">Contact</button>
                        </a>
                    </div>
                    <div v-if="currentTab === 'Standard'" class="p-4">
                        <h1 class="text-2xl font-bold mb-2">Standard</h1>
                        <p class="text-lg font-semibold text-gray-400">{{ serviceData.standardPrice || '$50' }}</p>
                        <ul class="list-disc list-inside mt-4 text-gray-400">
                            <li v-for="item in serviceData.standardInfo || []" :key="item">{{ item }}</li>
                        </ul>
                        <a href="https://t.me/borin_morm" target="_blank" rel="noopener noreferrer">
                            <button
                                class="mt-4 w-full bg-cyan-600 text-white py-2 rounded hover:bg-cyan-700">Contact</button>
                        </a>
                    </div>
                    <div v-if="currentTab === 'Premium'" class="p-4">
                        <h1 class="text-2xl font-bold mb-2">Premium</h1>
                        <p class="text-lg font-semibold text-gray-400">{{ serviceData.premiumPrice || '$95' }}</p>
                        <ul class="list-disc list-inside mt-4 text-gray-400">
                            <li v-for="item in serviceData.premiumInfo || []" :key="item">{{ item }}</li>
                        </ul>
                        <a href="https://t.me/borin_morm" target="_blank" rel="noopener noreferrer">
                            <button
                                class="mt-4 w-full bg-cyan-600 text-white py-2 rounded hover:bg-cyan-700">Contact</button>
                        </a>
                    </div>
                </div>
            </div>
        </main>

        <!-- Image Popup Modal -->
        <div v-if="showPopup" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            @click="closePopup">
            <div class="relative max-w-4xl max-h-[80vh] p-4">
                <!-- Close Button -->
                <button class="absolute top-2 right-2 text-white text-2xl font-bold hover:text-gray-300 transition-colors z-10" 
                        @click.stop="closePopup">×</button>
                
                <!-- Left Arrow -->
                <button v-if="serviceImages.length > 1" 
                        class="absolute -left-16 top-1/2 transform -translate-y-1/2 text-white text-4xl font-bold hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70 modal-arrow"
                        @click.stop="previousImage">
                    ‹
                </button>
                
                <!-- Right Arrow -->
                <button v-if="serviceImages.length > 1" 
                        class="absolute -right-16 top-1/2 transform -translate-y-1/2 text-white text-4xl font-bold hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70 modal-arrow"
                        @click.stop="nextImage">
                    ›
                </button>
                
                <!-- Image -->
                <img :src="popupImage" alt="Popup Image" class="w-full h-auto max-h-[80vh] object-contain rounded-md" />
                
                <!-- Image Counter -->
                <div v-if="serviceImages.length > 1" 
                     class="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
                    {{ modalImageIndex + 1 }} / {{ serviceImages.length }}
                </div>
            </div>
        </div>

        <ClientFooter />
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import Header from '@/components/Header.vue';
import ClientFooter from '@/components/ClientFooter.vue';
import { projectFirestore } from '@/firebase/config.js';
import { doc, getDoc, setDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';

// Background Image
import backgroundImage from '@/assets/images/background.png';
// Default Image
import defaultImage from '@/assets/images/default.png';

// Generate or retrieve anonymous user ID
const getAnonymousUserId = () => {
    let userId = localStorage.getItem('anonymousUserId');
    if (!userId) {
        userId = 'anon_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('anonymousUserId', userId);
    }
    return userId;
};

const route = useRoute();
const serviceData = ref({
    imageURLs: null,
    additionalImages: [],
    title: '',
    description: '',
    status: false,
    isOutstanding: false,
    demoLink: '',
    basicPrice: null,
    basicInfo: [],
    standardPrice: null,
    standardInfo: [],
    premiumPrice: null,
    premiumInfo: []
});
const serviceImages = ref([]);
const tabs = ['Basic', 'Standard', 'Premium'];
const currentTab = ref('Premium');
const showPopup = ref(false);
const popupImage = ref('');
const currentImageIndex = ref(0);
const modalImageIndex = ref(0);
const isLiked = ref(false);
const likeCount = ref(0);
const anonymousUserId = getAnonymousUserId();

// Computed property to ensure default image is always available
const getDefaultImage = computed(() => defaultImage);

const loadServiceData = async () => {
    try {
        const serviceRef = doc(projectFirestore, 'services', route.params.id);
        const serviceDoc = await getDoc(serviceRef);
        if (serviceDoc.exists()) {
            const data = serviceDoc.data();
            serviceData.value = {
                imageURLs: data.imageURLs || null,
                additionalImages: data.additionalImages || [],
                title: data.title || '',
                description: data.description,
                status: data.status || false,
                isOutstanding: data.isOutstanding || false,
                demoLink: data.demoLink || '',
                basicPrice: data.basicPrice || null,
                basicInfo: data.basicInfo || [],
                standardPrice: data.standardPrice || null,
                standardInfo: data.standardInfo || [],
                premiumPrice: data.premiumPrice || null,
                premiumInfo: data.premiumInfo || []
            };
            
            // Combine main image and additional images
            const images = [];
            
            // Handle main image - could be string or array
            if (data.imageURLs) {
                if (Array.isArray(data.imageURLs)) {
                    // If it's an array, filter out empty strings and add valid ones
                    const validMainImages = data.imageURLs.filter(img => img && typeof img === 'string' && img.trim() !== '');
                    images.push(...validMainImages);
                } else if (typeof data.imageURLs === 'string' && data.imageURLs.trim() !== '') {
                    // If it's a string, add it if not empty
                    images.push(data.imageURLs);
                }
            }
            
            // Handle additional images
            if (data.additionalImages && Array.isArray(data.additionalImages) && data.additionalImages.length > 0) {
                // Filter out empty or invalid URLs
                const validAdditionalImages = data.additionalImages.filter(img => img && typeof img === 'string' && img.trim() !== '');
                images.push(...validAdditionalImages);
            }
            
            serviceImages.value = images.length > 0 ? images : [getDefaultImage.value];
        } else {
            console.log('No service found');
            serviceImages.value = [getDefaultImage.value];
        }
    } catch (err) {
        console.error('Error loading service:', err);
        serviceImages.value = [getDefaultImage.value];
    }
};

const loadLikeStatus = async () => {
    try {
        const reactRef = doc(projectFirestore, 'reacts', `${anonymousUserId}_${route.params.id}`);
        const reactDoc = await getDoc(reactRef);
        isLiked.value = reactDoc.exists() && reactDoc.data().liked;
    } catch (err) {
        console.error('Error loading like status:', err);
    }
};

const loadLikeCount = async () => {
    try {
        const reactsQuery = query(
            collection(projectFirestore, 'reacts'),
            where('serviceId', '==', route.params.id),
            where('liked', '==', true)
        );
        const querySnapshot = await getDocs(reactsQuery);
        likeCount.value = querySnapshot.size;
    } catch (err) {
        console.error('Error loading like count:', err);
    }
};

const toggleLike = async () => {
    try {
        const reactRef = doc(projectFirestore, 'reacts', `${anonymousUserId}_${route.params.id}`);
        if (isLiked.value) {
            await deleteDoc(reactRef);
            isLiked.value = false;
            likeCount.value = Math.max(0, likeCount.value - 1);
        } else {
            await setDoc(reactRef, {
                serviceId: route.params.id,
                userId: anonymousUserId,
                liked: true,
                timestamp: new Date()
            });
            isLiked.value = true;
            likeCount.value += 1;
        }
    } catch (err) {
        console.error('Error toggling like:', err);
    }
};

const setCurrentImage = (index) => {
    currentImageIndex.value = index;
};

const openPopup = (image) => {
    popupImage.value = image || getDefaultImage.value;
    modalImageIndex.value = currentImageIndex.value;
    showPopup.value = true;
};

const closePopup = () => {
    showPopup.value = false;
    popupImage.value = '';
    modalImageIndex.value = 0;
};

const nextImage = () => {
    if (serviceImages.value.length > 1) {
        modalImageIndex.value = (modalImageIndex.value + 1) % serviceImages.value.length;
        popupImage.value = serviceImages.value[modalImageIndex.value] || getDefaultImage.value;
    }
};

const previousImage = () => {
    if (serviceImages.value.length > 1) {
        modalImageIndex.value = modalImageIndex.value === 0 
            ? serviceImages.value.length - 1 
            : modalImageIndex.value - 1;
        popupImage.value = serviceImages.value[modalImageIndex.value] || getDefaultImage.value;
    }
};

const openDemo = () => {
    if (serviceData.value.demoLink) {
        window.open(serviceData.value.demoLink, '_blank');
    } else {
        alert('No demo link available for this service.');
    }
};

onMounted(() => {
    loadServiceData();
    loadLikeStatus();
    loadLikeCount();
    
    // Add keyboard navigation
    const handleKeydown = (event) => {
        if (showPopup.value) {
            if (event.key === 'Escape') {
                closePopup();
            } else if (event.key === 'ArrowLeft') {
                previousImage();
            } else if (event.key === 'ArrowRight') {
                nextImage();
            }
        }
    };
    
    document.addEventListener('keydown', handleKeydown);
    
    // Cleanup
    onUnmounted(() => {
        document.removeEventListener('keydown', handleKeydown);
    });
});
</script>

<style scoped>
/* Ensure proper layout structure */
.min-h-screen {
    display: flex;
    flex-direction: column;
}

main {
    flex: 1;
    overflow: hidden;
}

/* Custom scrollbar for thumbnails */
.overflow-x-auto::-webkit-scrollbar {
    height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
    background: #2d2d44;
    border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
    background: #4a4a6a;
    border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #6a6a8a;
}

/* Ensure square aspect ratio */
.aspect-square {
    aspect-ratio: 1 / 1;
}

/* Modal arrow button styling */
.modal-arrow {
    backdrop-filter: blur(4px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 640px) {
    .flex-col {
        flex-direction: column;
    }

    .w-full.md\:w-1\/3 {
        width: 100%;
    }

    .w-full.md\:w-2\/3 {
        width: 100%;
    }
    
    /* Smaller thumbnails on mobile */
    .w-20 {
        width: 4rem;
    }
    
    .h-20 {
        height: 4rem;
    }
    
    /* Adjust arrow positioning for mobile */
    .-left-16 {
        left: -2rem;
    }
    
    .-right-16 {
        right: -2rem;
    }
    
    /* Smaller arrow buttons on mobile */
    .w-12 {
        width: 3rem;
    }
    
    .h-12 {
        height: 3rem;
    }
}

@media (min-width: 641px) {
    /* Medium thumbnails on tablet */
    .w-20 {
        width: 5rem;
    }
    
    .h-20 {
        height: 5rem;
    }
    
    /* Adjust arrow positioning for tablet */
    .-left-16 {
        left: -3rem;
    }
    
    .-right-16 {
        right: -3rem;
    }
}

@media (min-width: 1024px) {
    /* Larger thumbnails on desktop */
    .w-20 {
        width: 5rem;
    }
    
    .h-20 {
        height: 5rem;
    }
    
    /* Maximum arrow positioning for desktop */
    .-left-16 {
        left: -4rem;
    }
    
    .-right-16 {
        right: -4rem;
    }
}
</style>